import axiosInstance from "../api/axiosInstance";

export interface DiagnosticUploadInfo {
  id: number;
  uploadId: string;
  userId: number;
  username: string;
  email: string;
  platform: string;
  sessionId: number | null;
  deviceModel: string;
  appVersion: string;
  createdAt: string;
  analyzed: boolean;
  notes: string | null;
}

export interface DiagnosticUploadUpdate {
  analyzed: boolean;
  notes: string | null;
}

export interface DiagnosticStats {
  platform: string;
  deviceModel: string;
  uploadCount: number;
  uniqueUsers: number;
  analyzedCount: number;
  lastUpload: string;
}

export interface DiagnosticFile {
  name: string;
  size: number;
  lastModified: number;
}

export const SupportService = {
  /**
   * List diagnostic uploads for a specific user
   */
  async listUserUploads(
    userId: number,
    options?: {
      platform?: string;
      analyzed?: boolean;
      limit?: number;
      offset?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }
  ): Promise<DiagnosticUploadInfo[]> {
    const params = new URLSearchParams();
    params.append("userId", String(userId));

    if (options?.platform) params.append("platform", options.platform);
    if (options?.analyzed !== undefined) params.append("analyzed", String(options.analyzed));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.offset) params.append("offset", String(options.offset));
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);

    const response = await axiosInstance.get(`/support/diagnostics/list?${params.toString()}`);
    return response.data;
  },

  /**
   * List all diagnostic uploads (admin)
   */
  async listAllUploads(options?: {
    userId?: number;
    username?: string;
    platform?: string;
    sessionId?: number;
    deviceModel?: string;
    appVersion?: string;
    analyzed?: boolean;
    fromDate?: number;
    toDate?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }): Promise<DiagnosticUploadInfo[]> {
    const params = new URLSearchParams();

    if (options?.userId) params.append("userId", String(options.userId));
    if (options?.username) params.append("username", options.username);
    if (options?.platform) params.append("platform", options.platform);
    if (options?.sessionId) params.append("sessionId", String(options.sessionId));
    if (options?.deviceModel) params.append("deviceModel", options.deviceModel);
    if (options?.appVersion) params.append("appVersion", options.appVersion);
    if (options?.analyzed !== undefined) params.append("analyzed", String(options.analyzed));
    if (options?.fromDate) params.append("fromDate", String(options.fromDate));
    if (options?.toDate) params.append("toDate", String(options.toDate));
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.offset) params.append("offset", String(options.offset));

    const response = await axiosInstance.get(`/support/diagnostics/list?${params.toString()}`);
    return response.data;
  },

  /**
   * Search diagnostic uploads
   */
  async searchUploads(
    query: string,
    options?: {
      platform?: string;
      fromDate?: number;
      toDate?: number;
      limit?: number;
    }
  ): Promise<DiagnosticUploadInfo[]> {
    const params = new URLSearchParams();
    params.append("q", query);

    if (options?.platform) params.append("platform", options.platform);
    if (options?.fromDate) params.append("fromDate", String(options.fromDate));
    if (options?.toDate) params.append("toDate", String(options.toDate));
    if (options?.limit) params.append("limit", String(options.limit));

    const response = await axiosInstance.get(`/support/diagnostics/search?${params.toString()}`);
    return response.data;
  },

  /**
   * Get upload statistics
   */
  async getStats(options?: {
    fromDate?: number;
    toDate?: number;
  }): Promise<DiagnosticStats[]> {
    const params = new URLSearchParams();

    if (options?.fromDate) params.append("fromDate", String(options.fromDate));
    if (options?.toDate) params.append("toDate", String(options.toDate));

    const response = await axiosInstance.get(`/support/diagnostics/stats?${params.toString()}`);
    return response.data;
  },

  /**
   * List files in a diagnostic upload with metadata
   */
  async listFiles(uploadId: string): Promise<DiagnosticFile[]> {
    const response = await axiosInstance.get(`/support/diagnostics/files?uploadId=${encodeURIComponent(uploadId)}`);
    return response.data;
  },

  /**
   * Download a file and return its content as text
   */
  async downloadFile(uploadId: string, filename: string): Promise<string> {
    const response = await axiosInstance.get(
      `/support/diagnostics/download?uploadId=${encodeURIComponent(uploadId)}&filename=${encodeURIComponent(filename)}`,
      { responseType: "text" }
    );
    return response.data;
  },

  /**
   * Get download URL for a file (for direct browser download)
   */
  getDownloadUrl(uploadId: string, filename: string): string {
    const baseUrl = axiosInstance.defaults.baseURL || "";
    const userJson = localStorage.getItem("user-object");
    const user = userJson ? JSON.parse(userJson) : null;
    const accessToken = user?.accesstoken || "";
    return `${baseUrl}/support/diagnostics/download?uploadId=${encodeURIComponent(uploadId)}&filename=${encodeURIComponent(filename)}&accesstoken=${accessToken}`;
  },

  /**
   * Get download URL for all files as ZIP (for direct browser download)
   */
  getDownloadAllUrl(uploadId: string): string {
    const baseUrl = axiosInstance.defaults.baseURL || "";
    const userJson = localStorage.getItem("user-object");
    const user = userJson ? JSON.parse(userJson) : null;
    const accessToken = user?.accesstoken || "";
    return `${baseUrl}/support/diagnostics/downloadAll?uploadId=${encodeURIComponent(uploadId)}&accesstoken=${accessToken}`;
  },

  /**
   * Update diagnostic upload (notes and analyzed status)
   */
  async updateUpload(uploadId: string, update: DiagnosticUploadUpdate): Promise<void> {
    await axiosInstance.put(`/support/diagnostics/update?uploadId=${encodeURIComponent(uploadId)}`, update);
  },
};
